'use client'

import * as stylex from '@stylexjs/stylex'
import type { ComponentPropsWithRef, ReactNode } from 'react'

import { colors, controlSizes, fonts, fontSizes, radii, spacing } from '../tokens/tokens.stylex'

// Pending fieldsets retain checkbox colors; explicit disabling dims the control.
const styles = stylex.create({
  label: {
    alignItems: 'center',
    color: { default: colors.foreground, ':has(input[disabled])': colors.muted },
    cursor: { default: 'pointer', ':has(input[disabled])': 'not-allowed' },
    display: 'inline-flex',
    fontFamily: fonts.sans,
    fontSize: fontSizes.label,
    gap: spacing.space8,
    lineHeight: 1.5,
  },
  control: {
    alignItems: 'center',
    backgroundColor: {
      default: colors.background,
      ':has(input:checked)': colors.selected,
      ':has(input:indeterminate)': colors.selected,
    },
    borderColor: {
      default: colors.borderStrong,
      ':has(input:checked)': colors.selected,
      ':has(input:indeterminate)': colors.selected,
    },
    borderRadius: radii.small,
    borderStyle: 'solid',
    borderWidth: 1,
    boxSizing: 'border-box',
    color: colors.inverse,
    display: 'inline-flex',
    flexShrink: 0,
    height: controlSizes.checkbox,
    justifyContent: 'center',
    lineHeight: 1,
    opacity: { default: 1, ':has(input[disabled])': 0.5 },
    outline: { default: 'none', ':has(input:focus-visible)': `2px solid ${colors.foreground}` },
    outlineOffset: 3,
    position: 'relative',
    width: controlSizes.checkbox,
  },
  input: {
    cursor: 'inherit',
    height: '100%',
    inset: 0,
    margin: 0,
    opacity: 0,
    position: 'absolute',
    width: '100%',
  },
  checked: { display: { default: 'none', ':is(input:checked:not(:indeterminate) + *)': 'inline' } },
  mixed: { display: { default: 'none', ':is(input:indeterminate ~ *)': 'inline' } },
})

type NativeCheckboxProps = Omit<
  ComponentPropsWithRef<'input'>,
  'checked' | 'defaultChecked' | 'children' | 'className' | 'style' | 'type' | 'size'
>

type ControlledCheckboxState = { checked: boolean; defaultChecked?: never; onCheckedChange(checked: boolean): void }

type UncontrolledCheckboxState = { checked?: never; defaultChecked?: boolean; onCheckedChange?(checked: boolean): void }

type CheckboxState = ControlledCheckboxState | UncontrolledCheckboxState
type VisibleLabel = Exclude<ReactNode, boolean | null | undefined>
type VisibleCheckboxLabel = { children: VisibleLabel; 'aria-label'?: string }

type AccessibleCheckboxLabel = { children?: never; 'aria-label': string }

type CheckboxLabel = VisibleCheckboxLabel | AccessibleCheckboxLabel

type CheckboxOptions = { indeterminate?: boolean }

export type CheckboxProps = NativeCheckboxProps & CheckboxState & CheckboxLabel & CheckboxOptions

/**
 * Choose independent options submitted with a form; use ToggleButton for an immediately applied pressed
 * action. Applications supply the accessible label and own any controlled checked value. Native form
 * association and reset semantics remain available.
 *
 * Status: supported.
 */
export function Checkbox({ children, ref, indeterminate = false, onChange, onCheckedChange, ...props }: CheckboxProps) {
  return (
    <label {...stylex.props(styles.label)}>
      <span {...stylex.props(styles.control)}>
        <input
          {...props}
          type="checkbox"
          ref={(element) => {
            // oxlint-disable-next-line no-param-reassign -- Native checkbox indeterminate state is an imperative DOM property.
            if (element) element.indeterminate = indeterminate

            if (typeof ref === 'function') return ref(element)

            // oxlint-disable-next-line no-param-reassign -- React writable refs publish the mounted input to the caller.
            if (ref) ref.current = element
          }}
          onChange={(event) => {
            onChange?.(event)
            onCheckedChange?.(event.target.checked)
          }}
          {...stylex.props(styles.input)}
        />
        <span aria-hidden {...stylex.props(styles.checked)}>
          ✓
        </span>
        <span aria-hidden {...stylex.props(styles.mixed)}>
          −
        </span>
      </span>
      {children}
    </label>
  )
}
